import { useMemo, useCallback, useState, useEffect } from 'react';
import { useResidentConversations } from './useMessages';
import type { IConversation } from '@/types/admin/messages';
import type { MessageSortOption } from '@/constants/residents/messages';
import { formatRelativeDate } from '@/utils/common/formatting';

/**
 * Unified conversation type for property chat conversations
 */
export interface UnifiedConversation {
  id: string;
  type: 'property';
  title: string;
  preview: string;
  date: string;
  timestamp: number; // For sorting
  hasUnread: boolean;
  // Property chat specific
  isGroup?: boolean;
  recipientCount?: number;
  // Original data for navigation
  originalId: number;
}

/**
 * Hook that fetches property chat conversations.
 * Displays admin-resident conversations in the Property Chat screen.
 */
export const useCombinedConversations = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<MessageSortOption>('latest');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch property chat conversations
  const {
    conversations: propertyConversations,
    isLoading,
    refetchInitial: refetchPropertyConversations,
    setConversationQuery,
    setSortType,
    markConversationAsReadLocally,
  } = useResidentConversations();

  // Sync search query with property chat hook
  useEffect(() => {
    setConversationQuery(searchQuery);
  }, [searchQuery, setConversationQuery]);

  // Sync sort option with property chat hook
  useEffect(() => {
    setSortType(sortOption);
  }, [sortOption, setSortType]);

  // Transform property conversation to unified format
  const transformPropertyConversation = useCallback((conv: IConversation): UnifiedConversation => {
    const lastMessage = conv.messages?.[conv.messages.length - 1];
    const preview = lastMessage?.content || (lastMessage?.files?.length ? 'Attachment' : 'Send the first message...');
    const whenSource = (lastMessage as any)?.createdAt || (conv as any).updatedAt || (conv as any).createdAt;
    const timestamp = whenSource ? new Date(whenSource).getTime() : 0;
    
    // Check if it's a group message (multiple residents)
    const residents = (conv.accounts || []).filter(
      (a: any) => a.ConversationAccount?.type === 'resident'
    );
    const isGroup = residents.length > 1;

    return {
      id: `property-${conv.id}`,
      type: 'property',
      title: conv.subject || 'Conversation',
      preview,
      date: formatRelativeDate(whenSource),
      timestamp,
      hasUnread: (conv as any).hasUnread ?? false,
      isGroup,
      recipientCount: isGroup ? residents.length : undefined,
      originalId: conv.id as number,
    };
  }, []);

  // Transform and sort property conversations
  const conversations = useMemo(() => {
    // Transform property conversations
    const transformedProperty = propertyConversations.map(transformPropertyConversation);
    
    // Filter by search query if provided
    const filteredConversations = searchQuery
      ? transformedProperty.filter(conv => 
          conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          conv.preview.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : transformedProperty;
    
    // Sort conversations based on sort option
    return filteredConversations.sort((a, b) => {
      switch (sortOption) {
        case 'latest':
          return b.timestamp - a.timestamp;
        case 'aToZ':
          return a.title.localeCompare(b.title);
        case 'zToA':
          return b.title.localeCompare(a.title);
        default:
          return b.timestamp - a.timestamp;
      }
    });
  }, [
    propertyConversations, 
    searchQuery, 
    sortOption,
    transformPropertyConversation,
  ]);

  // Refetch conversations
  const refetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchPropertyConversations();
    } finally {
      setRefreshing(false);
    }
  }, [refetchPropertyConversations]);

  // Check if list is empty
  const isEmpty = conversations.length === 0 && !isLoading;

  // Mark a conversation as read locally (optimistic update)
  const markAsReadLocally = useCallback((conversationId: number) => {
    markConversationAsReadLocally(conversationId);
  }, [markConversationAsReadLocally]);

  return {
    conversations,
    isLoading,
    refreshing,
    isEmpty,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    refetchAll,
    markAsReadLocally,
  };
};

export default useCombinedConversations;
